import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError, eur } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Loader2, ShieldCheck, FileDown, CreditCard, Building2, Smartphone, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STAGES = ["Proposta inviata", "Accettata", "In attesa bonifico", "Pagata", "Polizza emessa"];

export default function ProposalAccept() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/proposta/${token}`);
      setData(data);
    } catch (e) { setErr(apiError(e)); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const dl = (kind) => window.open(`${BACKEND}/api/public/proposta/${token}/download/${kind}`, "_blank");

  const accettaOnline = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/public/proposta/${token}/accetta`);
      toast.success(res.message || "Proposta accettata");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const inviaOtp = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/public/proposta/${token}/otp/invia`);
      setOtpSent(true);
      toast.success(res.sms_inviato ? `Codice inviato via SMS al numero ${res.phone_mask}` : "Codice generato (SMS non disponibile: contatta il consulente)");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const accettaOtp = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/public/proposta/${token}/otp/accetta`, { code });
      toast.success(res.message || "Proposta accettata");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bone p-6" data-testid="proposal-error">
        <Card className="max-w-md rounded-2xl border-border/60 p-8 text-center">
          <p className="text-lg font-semibold text-forest">Proposta non disponibile</p>
          <p className="mt-2 text-sm text-muted-foreground">{err}</p>
        </Card>
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-bone"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  }

  return (
    <div className="min-h-screen bg-bone px-4 py-8">
      <div className="mx-auto w-full max-w-lg space-y-5">
        <Card className="rounded-2xl border-border/60 p-6" data-testid="proposal-card">
          <div className="mb-5 flex items-center gap-2 text-terracotta">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">{data.agency_name || "Assietica"}</span>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-forest">La tua proposta assicurativa</h1>

          {/* Avanzamento pratica */}
          <div className="mt-5" data-testid="proposal-progress">
            <div className="flex items-center">
              {STAGES.map((s, i) => (
                <div key={s} className="flex flex-1 items-center last:flex-none">
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold
                    ${i <= data.step ? "bg-forest text-white" : "bg-muted text-muted-foreground"}`}>
                    {i < data.step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </div>
                  {i < STAGES.length - 1 && <div className={`h-0.5 flex-1 ${i < data.step ? "bg-forest" : "bg-muted"}`} />}
                </div>
              ))}
            </div>
            <div className="mt-2 text-sm font-medium text-forest" data-testid="proposal-stage-label">{STAGES[data.step]}</div>
          </div>

          <div className="mt-5 space-y-2 rounded-xl bg-sage/50 p-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Prodotto</span><span className="font-medium text-forest">{data.product_name}</span></div>
            {data.contraente && <div className="flex justify-between"><span className="text-muted-foreground">Contraente</span><span className="font-medium text-forest">{data.contraente}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Premio annuo lordo</span><span className="font-semibold text-forest">{eur(data.premio)}</span></div>
          </div>
        </Card>

        {/* Documenti */}
        {data.documenti?.length > 0 && (
          <Card className="rounded-2xl border-border/60 p-6" data-testid="proposal-documenti">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest"><FileText className="h-4 w-4" /> Documenti</h2>
            <div className="grid gap-2">
              {data.documenti.map((d) => (
                <button key={d.kind} data-testid={`proposal-doc-${d.kind}`} onClick={() => dl(d.kind)}
                  className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-left text-sm text-forest transition-colors hover:border-forest/40 hover:bg-sage/30">
                  <FileDown className="h-4 w-4 text-terracotta" /> {d.label}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Pagamento */}
        {data.pagamento && (
          <Card className="rounded-2xl border-border/60 p-6" data-testid="proposal-pagamento">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest"><CreditCard className="h-4 w-4" /> Pagamento del premio</h2>
            {data.pagamento.mode === "partner" ? (
              <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="pagamento-partner">
                <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                <p className="text-sm text-amber-800">{data.pagamento.message}</p>
              </div>
            ) : (
              <div className="rounded-xl bg-sage/50 p-4 text-sm" data-testid="pagamento-agenzia">
                <p className="whitespace-pre-line text-forest">
                  {data.pagamento.messaggio || `Intestatario: ${data.pagamento.intestatario}\nIBAN: ${data.pagamento.iban}\nCausale: ${data.pagamento.causale}`}
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Polizza */}
        {data.polizza_disponibile && (
          <Card className="rounded-2xl border-border/60 p-6" data-testid="proposal-polizza">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest"><ShieldCheck className="h-4 w-4" /> La tua polizza</h2>
            <Button onClick={() => dl("polizza")} data-testid="proposal-polizza-download" className="gap-2 rounded-full bg-forest hover:bg-forest-hover">
              <FileDown className="h-4 w-4" /> Scarica la polizza
            </Button>
          </Card>
        )}

        {/* Accettazione */}
        <Card className="rounded-2xl border-border/60 p-6">
          {data.accettata ? (
            <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="proposal-accepted">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Proposta accettata</p>
                <p className="mt-1 text-sm text-emerald-700">Grazie! Il tuo referente riceverà conferma e proseguirà con l'emissione. Puoi tornare su questa pagina per seguire lo stato della pratica.</p>
              </div>
            </div>
          ) : data.accettabile ? (
            <>
              <p className="flex items-start gap-2 text-xs text-muted-foreground">
                <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Accettando confermi di aver ricevuto e letto il preventivo e la documentazione precontrattuale (Set informativo, DIP, DIP aggiuntivo, CGA, MUP).
              </p>
              {!otpMode ? (
                <div className="mt-4 space-y-2">
                  <Button onClick={accettaOnline} disabled={busy} data-testid="proposal-accept-button"
                    className="w-full gap-2 rounded-full bg-terracotta py-6 text-base hover:bg-terracotta/90">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Accetto la proposta
                  </Button>
                  {data.otp_disponibile && (
                    <Button variant="outline" onClick={() => { setOtpMode(true); }} data-testid="proposal-otp-toggle"
                      className="w-full gap-2 rounded-full border-forest/30 py-5 text-forest hover:bg-sage/40">
                      <Smartphone className="h-4 w-4" /> Accetto con codice OTP (firma elettronica)
                    </Button>
                  )}
                </div>
              ) : (
                <div className="mt-4 space-y-3" data-testid="proposal-otp-box">
                  {!otpSent ? (
                    <Button onClick={inviaOtp} disabled={busy} data-testid="proposal-otp-send"
                      className="w-full gap-2 rounded-full bg-forest py-5 hover:bg-forest-hover">
                      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Smartphone className="h-5 w-5" />} Inviami il codice via SMS
                    </Button>
                  ) : (
                    <>
                      <Input data-testid="proposal-otp-input" value={code} onChange={(e) => setCode(e.target.value)}
                        placeholder="Codice a 6 cifre" inputMode="numeric" maxLength={6} className="text-center text-lg tracking-widest" />
                      <Button onClick={accettaOtp} disabled={busy || code.length < 6} data-testid="proposal-otp-confirm"
                        className="w-full gap-2 rounded-full bg-terracotta py-5 hover:bg-terracotta/90">
                        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Conferma e accetta
                      </Button>
                      <button onClick={inviaOtp} disabled={busy} className="w-full text-center text-xs text-muted-foreground underline">Non ho ricevuto il codice, invia di nuovo</button>
                    </>
                  )}
                  <button onClick={() => { setOtpMode(false); setOtpSent(false); setCode(""); }} className="w-full text-center text-xs text-muted-foreground">Torna indietro</button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">La proposta è in lavorazione. Torna su questa pagina per seguire lo stato della pratica.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
